import { useEffect } from "react";
import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";

import {
  Alert,
  Loader,
  RoundedBox,
  Typography,
} from "@braintrust/braintrust-ui-components";
import { showAlert, useMediaQuery } from "@braintrust/braintrust-ui-components";
import { ACCESS_DENIED } from "@js/apps/common/constants";
import { JobEmployerDetails } from "@js/apps/employer/components";
import { ExperienceBox } from "@js/apps/jobs/components/experience-box";
import { RoleAndJobTypeBox } from "@js/apps/jobs/components/role-and-job-type-box";
import { JOB_LOCATION, JobLocationContext } from "@js/apps/jobs/context";
import { ExplainerCarousel } from "@js/apps/jobs/views/public-job-details/explainer-carousel";
import { PageTitle } from "@js/components/page-title";
import { PublicTopNavigation } from "@js/components/public-top-navigation";
import { useAppDispatch, useNavigate } from "@js/hooks";
import { globalTimeoutsDelays, useTimeout } from "@js/hooks/timeout";
import { AppLayout } from "@js/layouts/app";
import {
  ConversionEvents,
  pushConversionEvent,
} from "@js/services/analytics/conversion-tracking";

import { viewJobDetails } from "../../actions";
import { PublicJobsSeeAllJobsButton } from "../../apps/public-listing/components";
import { JobDetailsBasicDetails, JobHeader } from "../../components";
import { JobDescription } from "../../components/job-description";
import { PublicSimilarJobsSection } from "../../components/public-similar-jobs-carousel";
import { usePublicJobDetails } from "../../hooks/use-public-job-details";

import {
  PublicJobStatus,
  PublicRateComponent,
  SocialProofModule,
} from "./components";

import style from "../job-details/style.module.scss";
import publicStyle from "./style.module.scss";

type PublicJobDetailsProps = { jobId: number };

export const PublicJobDetails = ({ jobId }: PublicJobDetailsProps) => {
  const md = useMediaQuery("md");
  const dispatch = useAppDispatch();
  const {
    loading,
    job,
    employerProfile: employer,
    error,
  } = usePublicJobDetails({ jobId });
  const navigate = useNavigate();

  useTimeout(
    () => {
      if (!jobId) {
        return;
      }
      dispatch(
        viewJobDetails({
          source: JOB_LOCATION.public_job_details,
          jobUrl: window.location.href,
          jobId,
        }),
      );
    },
    globalTimeoutsDelays.log_view_event,
    [dispatch, jobId],
  );

  useEffect(() => {
    if (!job) {
      return;
    }
    pushConversionEvent(ConversionEvents.VACANCY_VIEW);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [job?.id]);

  useEffect(() => {
    if (error) {
      const status = (error as FetchBaseQueryError)?.status;

      if (status === 401) {
        navigate(`/access-denied/?message=${ACCESS_DENIED.JOB_ACCESS_DENIED}`, {
          replace: true,
        });
        return;
      }

      navigate(`/jobs/`);
    }
  }, [error, navigate]);

  return (
    <JobLocationContext.Provider value={JobLocationContext.Values.job_details}>
      <AppLayout
        className={style.job}
        bgcolor="var(--soft-blue)"
        renderOverwriteNavigationComponent={() => <PublicTopNavigation />}
      >
        {loading ? (
          <Loader centered />
        ) : (
          job && (
            <>
              <PageTitle>{`Job: ${job.title}`}</PageTitle>
              {!!job.referring_user &&
                showAlert(
                  <Alert type="info" withIcon>
                    <Typography
                      component="p"
                      variant="paragraph"
                      size="medium"
                      sx={{ whiteSpace: "pre-wrap" }}
                    >
                      <Typography component="span" fontWeight={500}>
                        {job.referring_user}
                      </Typography>{" "}
                      referred you for this job, please review and place an
                      application if you think you’d be a good fit!
                    </Typography>
                  </Alert>,
                )}
              <main>
                <PublicJobsSeeAllJobsButton />
                <JobHeader job={job} />
                <section className={publicStyle.jobDetails}>
                  <div
                    className={`${style.jobBasicDetails} job-overwrite-basic-details`}
                  >
                    <JobDetailsBasicDetails
                      boxProps={{
                        className: style.jobBasicDetailsWrapper,
                      }}
                      job={job}
                    />
                  </div>
                  <div className={style.jobTopSkills}>
                    <RoleAndJobTypeBox
                      jobRole={job.role}
                      jobType={job.job_type}
                    />
                  </div>
                  <div className={style.jobSeniority}>
                    <ExperienceBox
                      experienceAsArrow={job.experience_as_arrow}
                      experienceLevel={job.experience_level}
                      jobRole={job.role}
                    />
                  </div>
                  <div className={`${style.jobRate} job-overwrite-rate`}>
                    <PublicRateComponent job={job} />
                  </div>
                  <div className={publicStyle.jobStatusBox}>
                    <PublicJobStatus job={job} />
                  </div>
                  {md && (
                    <div className={publicStyle.jobExplainerCarousel}>
                      <ExplainerCarousel job={job} />
                    </div>
                  )}
                </section>
                <section className={publicStyle.jobContent}>
                  <section style={{ overflow: "hidden" }}>
                    <JobDescription job={job} />
                    <SocialProofModule
                      jobId={job.id}
                      jobStatus={job.job_status}
                    />
                  </section>
                  <section className={style.jobEmployerColumn}>
                    {!md && <ExplainerCarousel job={job} />}
                    <div>
                      {!employer ? (
                        <Loader />
                      ) : (
                        <RoundedBox>
                          <JobEmployerDetails
                            employer={employer}
                            jobId={job.id}
                            hideOpenJobs
                          />
                        </RoundedBox>
                      )}
                    </div>
                  </section>
                </section>
                <section className={publicStyle.jobSimilarJobs}>
                  <PublicSimilarJobsSection jobId={job.id} />
                </section>
              </main>
            </>
          )
        )}
      </AppLayout>
    </JobLocationContext.Provider>
  );
};
